import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { PVC_TEST_ID, TestProvider } from '../../providers/test-provider';
import { UtilsProvider } from "../../../common/providers/utils-provider.service";
import { AthletesProvider } from '../../../athletes/providers/athletes-provider';
import { Observable, Subscription } from 'rxjs';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { shareReplay, tap, withLatestFrom } from 'rxjs/operators';
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { TranslateModule } from "@ngx-translate/core";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { AsyncPipe, NgFor, NgIf } from "@angular/common";
import { MatButtonModule } from "@angular/material/button";

@Component({
  selector: 'new-measure-form',
  templateUrl: './new-measure-form.component.html',
  styleUrls: ['./new-measure-form.scss'],
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    TranslateModule,
    MatDatepickerModule,
    NgIf,
    NgFor,
    AsyncPipe,
    MatButtonModule,
  ],
  standalone: true
})
export class NewMeasureFormComponent implements OnInit, OnDestroy {

    public form: FormGroup;
    public isSubmitting = false;
    public minDate: any;
    public maxDate: any;
    public startDate = new Date();
    public readonly DESCRIPTION_MAX_LENGTH: number = 250;
    private subscriptions: Subscription[] = [];

    public metrics$: Observable<any>;
    public requiresBirthday = false;

    public get values() : FormArray {
        return this.form.get("values") as FormArray;
    }

    constructor(
        public dialog: MatDialogRef<NewMeasureFormComponent>,
        private fb: FormBuilder,
        private toaster: MatSnackBar,
        private utils: UtilsProvider,
        private testProvider: TestProvider,
        private utilsProvider: UtilsProvider,
        private athleteProvider: AthletesProvider,
        @Inject(MAT_DIALOG_DATA) public data: any
    ) {
        this.metrics$ = data.metrics$.pipe(
            tap(metrics => {
                for (let metric of metrics as []) {
                    this.values.push(this.createValue(metric));
                }
            }),
            shareReplay()
        );

        this.requiresBirthday = data['test'].id === PVC_TEST_ID
            && !data['athlete'].athlete?.birthday;

        const formControls: any = {
            measureId: new FormControl(data['measureId'] ?? null),
            organizationId: new FormControl(data['organization'].id, [Validators.required]),
            athleteId: new FormControl(data['athlete'].athlete.id, [Validators.required]),
            testId: new FormControl(data['test'].id, [Validators.required]),
            date: new FormControl(new Date(), [Validators.required]),
            values: this.fb.array([]),
            size: new FormControl(data['athlete'].size, [Validators.required, Validators.min(0)]),
            weight: new FormControl(data['athlete'].weight, [Validators.required, Validators.min(0)]),
            comment: new FormControl('', [Validators.maxLength(this.DESCRIPTION_MAX_LENGTH)]),
        };

        if (this.requiresBirthday) {
            formControls['birthday'] = new FormControl(null, [Validators.required]);
        }

        this.form = this.fb.group(formControls);

        // form edit
        let data$: Observable<any> = data['data$'];
        if (data$) {
            const isPvc = data['test'].id === PVC_TEST_ID;
            this.subscriptions.push(
                data$.pipe(withLatestFrom(this.metrics$)).subscribe(([measures, _metrics]) => {
                    let m: any;
                    if (isPvc) {
                        m = (measures as any[]).find((item: any) => item.id === data['measureId']);
                    } else {
                        m = this.utils.findIn(data['measureId'], measures.selected, 'measureId');
                    }

                    if (!m) return;

                    this.form.patchValue({
                        measureId: data['measureId'],
                        date: m.date,
                        size: m.size,
                        weight: m.weight,
                        comment: m.comment,
                    });

                    for (let i = 0; i < this.values.length; i++) {
                        const metricId = this.values.at(i).value['metric'];
                        if (isPvc && m.values) {
                            const val = m.values.find((v: any) => v.test_metric_id === metricId);
                            if (val) {
                                this.values.at(i).patchValue({data: val.value});
                            }
                        } else if (m.measures) {
                            this.values.at(i).patchValue({data: m.measures[metricId].value});
                        }
                    }
                })
            );
        } else {
            this.athleteProvider.get(data['organization'].id, data['athlete'].id).subscribe({
                next: user => {
                    this.form.patchValue({
                        size: user.athlete?.size,
                        weight: user.athlete?.weight
                    });
                }
            })
        }
    }

    ngOnInit(): void {
        this.subscriptions.push(this.metrics$.subscribe());
    }


    public createValue(metric: any) {
        return this.fb.group({
            data: new FormControl(null, [Validators.required]),
            metric: new FormControl(metric.id)
        });
    }

    public ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    public submitForm() {
        this.isSubmitting = true;
        if (this.form.valid) {
            // convert date manually to remove TZ
            this.form.value.date = this.utilsProvider.DateToYmdHis(new Date(this.form.value.date), false);
            
            if (this.form.value.birthday) {
                this.form.value.birthday = this.utilsProvider.DateToYmdHis(new Date(this.form.value.birthday), false);
            }

            this.testProvider.saveMeasure(
                this.data.organization,
                this.data.athlete,
                this.data.test,
                this.form.value
            ).subscribe(measure => {
                this.dialog.close(measure);
            });
        } else {
            this.form.markAllAsTouched();
            this.isSubmitting = false;
        }
    }

    get f() {
        return this.form.controls;
    }
}
